/**
 * Observability shim — PostHog (analytics) + Sentry (errors).
 *
 * CLAUDE.md mandates both. This module is a soft-fail wrapper:
 *   - Activates only if the env var for each SDK is set.
 *   - Uses dynamic import() so apps that haven't installed the SDK
 *     don't break — the import silently fails and we no-op.
 *
 * To enable, install the SDKs in the consuming app:
 *
 *     npm install posthog-js @sentry/react
 *
 * Then set VITE_POSTHOG_KEY (and optionally VITE_POSTHOG_HOST) and
 * VITE_SENTRY_DSN in the env. Call `initObservability({ app })` once
 * at startup. Use `captureException(err)` and `track(event, props)`
 * from anywhere — they are silent no-ops when SDKs are absent.
 */

interface InitArgs {
  /** App name written into every event for filtering: 'customer-portal' | 'admin' | 'onsite' | 'estimator' */
  app: string;
  /** Optional release version; defaults to import.meta.env.VITE_RELEASE or 'unknown' */
  release?: string;
  /** Optional explicit user id; set on auth */
  user?: { id: string; email?: string; role?: string } | null;
}

type EventProps = Record<string, string | number | boolean | null | undefined>;

let posthog: { capture: (e: string, p?: EventProps) => void; identify: (id: string, p?: EventProps) => void; reset: () => void } | null = null;
let sentry: { captureException: (e: unknown, ctx?: Record<string, unknown>) => void; setUser: (u: Record<string, unknown> | null) => void } | null = null;
let inited = false;

function env(key: string): string | undefined {
  try {
    return (import.meta as any).env?.[key];
  } catch {
    return undefined;
  }
}

export async function initObservability(args: InitArgs): Promise<void> {
  if (inited) return;
  inited = true;
  const release = args.release ?? env('VITE_RELEASE') ?? 'unknown';

  // PostHog
  const posthogKey = env('VITE_POSTHOG_KEY');
  if (posthogKey) {
    try {
      // Indirect specifier so tsc does not attempt to resolve the
      // package at type-check time. The dynamic import resolves at
      // runtime and silently fails when the SDK isn't installed.
      const posthogSpecifier = 'posthog-js';
      const mod = (await import(/* @vite-ignore */ posthogSpecifier)) as {
        default: {
          init: (k: string, opts: Record<string, unknown>) => void;
          capture: (e: string, p?: EventProps) => void;
          identify: (id: string, p?: EventProps) => void;
          reset: () => void;
        };
      };
      mod.default.init(posthogKey, {
        api_host: env('VITE_POSTHOG_HOST') ?? 'https://us.i.posthog.com',
        person_profiles: 'identified_only',
        loaded: () => {
          if (args.user?.id) {
            mod.default.identify(args.user.id, {
              email: args.user.email,
              role: args.user.role,
              app: args.app,
            });
          }
        },
      });
      posthog = mod.default;
      posthog.capture('app_loaded', { app: args.app, release });
    } catch {
      // posthog-js not installed yet; silently skip.
    }
  }

  // Sentry
  const sentryDsn = env('VITE_SENTRY_DSN');
  if (sentryDsn) {
    try {
      const sentrySpecifier = '@sentry/react';
      const mod = (await import(/* @vite-ignore */ sentrySpecifier)) as {
        init: (opts: Record<string, unknown>) => void;
        captureException: (e: unknown, ctx?: Record<string, unknown>) => void;
        setUser: (u: Record<string, unknown> | null) => void;
      };
      mod.init({
        dsn: sentryDsn,
        release,
        environment: env('VITE_ENV') ?? 'development',
        tracesSampleRate: 0.1,
      });
      if (args.user) {
        mod.setUser({ id: args.user.id, email: args.user.email });
      }
      sentry = { captureException: mod.captureException, setUser: mod.setUser };
    } catch {
      // @sentry/react not installed yet; silently skip.
    }
  }
}

export function track(event: string, props?: EventProps): void {
  try {
    posthog?.capture(event, props);
  } catch {
    /* swallow */
  }
}

export function captureException(err: unknown, ctx?: Record<string, unknown>): void {
  try {
    sentry?.captureException(err, ctx);
  } catch {
    /* swallow */
  }
  // Always still log to the console — observability shouldn't suppress
  // dev signals when SDKs are off.
  console.error('[observability]', err, ctx);
}

export function setObservabilityUser(
  user: { id: string; email?: string; role?: string } | null,
): void {
  try {
    if (user) {
      posthog?.identify(user.id, { email: user.email, role: user.role });
      sentry?.setUser({ id: user.id, email: user.email });
    } else {
      posthog?.reset();
      sentry?.setUser(null);
    }
  } catch {
    /* swallow */
  }
}
